import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Typography,
    TextField,
    Grid,
    MenuItem,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Dialog,
    DialogContent,
    IconButton,
} from "@mui/material";
import toast from "react-hot-toast";
import configServ from "../services/config";
import { appServiceName } from "../services/http";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";


// Build the base static URL (without the /api part)
const staticBase = appServiceName.replace(/\/api\/?$/, '');

const resolveImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) {
        return url;
    }

    const base = staticBase.endsWith('/') ? staticBase.slice(0, -1) : staticBase;
    const path = url.startsWith('/') ? url : '/' + url;
    return base + path;
};
// Standalone Account Details Page
const AccountDetailsPage = () => {

    const { company_id: companyId, userId: employeeId } = useSelector(
        (state) => state.globalVariables
    );

    const [loading, setLoading] = useState(false);

    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        account_holder_name: "",
        bank_name: "",
        branch_name: "",
        ifsc_code: "",
        account_type: "savings",
        bank_account_number: "",
    });

    // Passbook / Cheque image
    const [passbookImage, setPassbookImage] = useState(null); 
    const [passbookPreview, setPassbookPreview] = useState(""); 
    const [imageLoadError, setImageLoadError] = useState(false); 

    // Popup / lightbox state
    const [previewOpen, setPreviewOpen] = useState(false);

    // Handle Input
    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Remove error while typing
        setErrors((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    // Handle Passbook / Cheque Image Upload
    const handlePassbookChange = (e) => {
        const file = e.target.files[0];

        if (!file) {
            return;
        }

        // Only allow image files
        if (!file.type.startsWith("image/")) {
            setErrors((prev) => ({
                ...prev,
                passbook_image: "Only image files are allowed",
            }));
            return;
        }

        setPassbookImage(file);
        setPassbookPreview(URL.createObjectURL(file));
        setImageLoadError(false);

        setErrors((prev) => ({
            ...prev,
            passbook_image: "",
        }));

        // allow re-selecting the same file again later
        e.target.value = "";
    };

    // Validate Form
    const validateForm = () => {
        let newErrors = {};

        if (!formData.account_holder_name.trim()) {
            newErrors.account_holder_name =
                "Account holder name is required";
        }

        if (!formData.bank_name.trim()) {
            newErrors.bank_name = "Bank name is required";
        }

        if (!formData.branch_name.trim()) {
            newErrors.branch_name = "Branch name is required";
        }

        if (!formData.bank_account_number.trim()) {
            newErrors.bank_account_number =
                "Bank account number is required";
        }

        if (!formData.ifsc_code.trim()) {
            newErrors.ifsc_code = "IFSC code is required";
        }

        if (!formData.account_type.trim()) {
            newErrors.account_type = "Account type is required";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    // Fetch Account Details
    const fetchAccountDetails = async () => {
        try {
            setLoading(true);

            const payload = {
                company_id: companyId,
                employee_id: employeeId,
            };

            const response =
                await configServ.getEmployeeAccountDetails(payload);

            if (response.status && response.data) {
                setFormData({
                    account_holder_name:
                        response.data.account_holder_name || "",

                    bank_name: response.data.bank_name || "",

                    branch_name: response.data.branch_name || "",

                    ifsc_code: response.data.ifsc_code || "",

                    account_type:
                        response.data.account_type || "savings",

                    bank_account_number:
                        response.data.bank_account_number || "",
                });

                if (response.data.image_url) {
                    setPassbookPreview(
                        resolveImageUrl(response.data.image_url)
                    );
                    setImageLoadError(false);
                } else {
                    setPassbookPreview("");
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    // Save / Update
    const handleSubmit = async () => {
        // Stop API call if validation fails
        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);

            // Using FormData since an image file may be included
            const payload = new FormData();

            payload.append("company_id", companyId);
            payload.append("employee_id", employeeId);

            Object.keys(formData).forEach((key) => {
                payload.append(key, formData[key]);
            });

            // Only attach if a new image was selected
            // NOTE: field name must EXACTLY match what the backend
            // expects. If the image saves but never comes back on
            // fetch, this key name is the first thing to check.
            if (passbookImage) {
                payload.append("image", passbookImage);
            }

            const response =
                await configServ.saveEmployeeAccountDetails(payload);

            if (response.status) {
                toast.success(response.message);

                // Reset the picked File so preview keeps showing the
                // server-returned image_url after refetch
                setPassbookImage(null);

                fetchAccountDetails();
            }
        } catch (error) {
            console.log(error);

            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (companyId && employeeId) {
            fetchAccountDetails();
        }
    }, [companyId, employeeId]);

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Card
                elevation={3}
                sx={{
                    borderRadius: 3,
                }}
            >
                <CardContent>
                    <Typography
                        variant="h6"
                        color="primary"
                        gutterBottom
                        fontWeight={600}
                    >
                        Account Details
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                    >
                        Manage employee banking information.
                    </Typography>

                    <Grid container spacing={2}>
                        {/* Account Holder Name */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Account Holder Name"
                                name="account_holder_name"
                                value={formData.account_holder_name}
                                onChange={handleChange}
                                error={!!errors.account_holder_name}
                                helperText={errors.account_holder_name}
                            />
                        </Grid>

                        {/* Bank Name */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Bank Name"
                                name="bank_name"
                                value={formData.bank_name}
                                onChange={handleChange}
                                error={!!errors.bank_name}
                                helperText={errors.bank_name}
                            />
                        </Grid>

                        {/* Branch Name */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Branch Name"
                                name="branch_name"
                                value={formData.branch_name}
                                onChange={handleChange}
                                error={!!errors.branch_name}
                                helperText={errors.branch_name}
                            />
                        </Grid>

                        {/* Account Number */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Bank Account Number"
                                name="bank_account_number"
                                value={formData.bank_account_number}
                                onChange={handleChange}
                                error={!!errors.bank_account_number}
                                helperText={errors.bank_account_number}
                            />
                        </Grid>

                        {/* IFSC Code */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="IFSC Code"
                                name="ifsc_code"
                                value={formData.ifsc_code}
                                onChange={handleChange}
                                error={!!errors.ifsc_code}
                                helperText={errors.ifsc_code}
                            />
                        </Grid>

                        {/* Account Type */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Account Type"
                                name="account_type"
                                value={formData.account_type}
                                onChange={handleChange}
                                error={!!errors.account_type}
                                helperText={errors.account_type}
                            >
                                <MenuItem value="savings">Savings</MenuItem>

                                <MenuItem value="current">Current</MenuItem>
                            </TextField>
                        </Grid>

                        {/* Passbook / Cheque Upload Button */}
                        <Grid item xs={12} md={6}>
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                startIcon={<CloudUploadIcon />}
                                sx={{
                                    height: 56,
                                    justifyContent: "flex-start",
                                    textTransform: "none",
                                }}
                            >
                                {passbookImage
                                    ? passbookImage.name
                                    : "Upload Passbook / Cheque"}
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={handlePassbookChange}
                                />
                            </Button>

                            {errors.passbook_image && (
                                <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ display: "block", mt: 0.5, ml: 1.5 }}
                                >
                                    {errors.passbook_image}
                                </Typography>
                            )}
                        </Grid>

                        {/* Preview Field - same size as upload button, click to open popup */}
                        <Grid item xs={12} md={6}>
                            <Box
                                onClick={() =>
                                    passbookPreview &&
                                    !imageLoadError &&
                                    setPreviewOpen(true)
                                }
                                sx={{
                                    height: 56,
                                    borderRadius: 1,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1.5,
                                    cursor:
                                        passbookPreview && !imageLoadError
                                            ? "pointer"
                                            : "default",
                                    bgcolor: "action.hover",
                                    overflow: "hidden",
                                    "&:hover":
                                        passbookPreview && !imageLoadError
                                            ? { borderColor: "primary.main" }
                                            : {},
                                }}
                            >
                                {passbookPreview && !imageLoadError ? (
                                    <>
                                        <Box
                                            component="img"
                                            key={passbookPreview}
                                            src={passbookPreview}
                                            alt="Passbook / Cheque"
                                            onError={() =>
                                                setImageLoadError(true)
                                            }
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                objectFit: "cover",
                                                borderRadius: 0.5,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            noWrap
                                        >
                                            Click to view full image
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <ImageIcon color="disabled" />
                                        <Typography
                                            variant="body2"
                                            color="text.disabled"
                                        >
                                            {imageLoadError
                                                ? "Image could not be loaded"
                                                : "No image uploaded yet"}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Submit Button */}
                    <Box
                        sx={{
                            mt: 4,
                            display: "flex",
                            justifyContent: "flex-end",
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                minWidth: 170,
                                height: 45,
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                "Save Details"
                            )}
                        </Button>
                    </Box>
                </CardContent>
            </Card>



            {/* Popup / Lightbox for full image */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: "90vw",
                        height: "90vh",
                        maxWidth: "90vw",
                        maxHeight: "90vh",
                        m: 0,
                    },
                }}
            >
                <IconButton
                    onClick={() => setPreviewOpen(false)}
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "background.paper",
                        boxShadow: 1,
                        "&:hover": { bgcolor: "background.paper" },
                        zIndex: 1,
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                <DialogContent
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 2,
                        height: "100%",
                        width: "100%",
                    }}
                >
                    <Box
                        component="img"
                        src={passbookPreview}
                        alt="Passbook / Cheque full preview"
                        sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: 1,
                        }}
                    />
                </DialogContent>
            </Dialog>
        </Container>
    );
};

export default AccountDetailsPage;